import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi, type RecoveryCandidate, type SandboxCheckout, type SandboxIntentRow, type SandboxQuote } from "../lib/admin-api";
import { toOpenableUrl } from "../lib/data-url";

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS = 10 * 60_000; // 10 minutes — bounded, never polls forever

const OBSERVATION_CHECKLIST = [
  "Exact checkout hostname (should be parcel2go.com / sandbox.parcel2go.com)",
  "Are sender/recipient/parcel details already filled in, or asked again?",
  "Is the selected courier/service shown correctly?",
  "What payment methods are offered (card, PayPal, other)?",
  "Does the page clearly say you're paying Parcel2Go, not this site?",
  "After paying, does the page redirect anywhere, or just show a Parcel2Go confirmation?",
  "Try reopening the same payment link — does it still work?",
  "If you cancel instead of paying, does the order stay unpaid (checked below)?",
];

export default function Parcel2GoSandboxTest() {
  const [health, setHealth] = useState<{ ok: boolean; detail?: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [quote, setQuote] = useState<SandboxQuote | null>(null);
  const [checkout, setCheckout] = useState<SandboxCheckout | null>(null);
  const [intent, setIntent] = useState<SandboxIntentRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<RecoveryCandidate[] | null>(null);
  const [previewQuotes, setPreviewQuotes] = useState<SandboxQuote[] | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [liveResult, setLiveResult] = useState<{ ok: boolean; detail: string; raw?: unknown } | null>(null);
  const [loadingLive, setLoadingLive] = useState(false);
  const pollStartedAt = useRef<number | null>(null);

  async function loadQuotesPreview() {
    setLoadingPreview(true);
    setError(null);
    try {
      const res = await adminApi.getParcel2GoQuotesPreview();
      setPreviewQuotes(res.quotes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't fetch quotes.");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function loadLiveQuotesPreview() {
    setLoadingLive(true);
    setLiveResult(null);
    try {
      const res = await adminApi.getParcel2GoLiveQuotesPreview();
      setLiveResult(res);
    } catch (err) {
      setLiveResult({ ok: false, detail: err instanceof Error ? err.message : "Couldn't fetch live quotes." });
    } finally {
      setLoadingLive(false);
    }
  }

  async function checkConnection() {
    setChecking(true);
    setError(null);
    try {
      const res = await adminApi.getIntegrationHealth();
      const p2g = res.providers.find((p) => p.id === "parcel2go");
      setHealth(p2g?.health ?? { ok: false, detail: "Parcel2Go provider not found." });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connection check failed.");
    } finally {
      setChecking(false);
    }
  }

  async function createTest(select?: { carrier: string; service: string }) {
    setCreating(true);
    setError(null);
    setQuote(null);
    setCheckout(null);
    setIntent(null);
    setCandidates(null);
    try {
      const res = await adminApi.createParcel2GoSandboxTest(select);
      setQuote(res.quote);
      setCheckout(res.checkout ?? null);
      pollStartedAt.current = Date.now();
      const intentRes = await adminApi.checkProviderPayment(res.shipmentIntentId);
      setIntent(intentRes.intent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create a sandbox test order.");
    } finally {
      setCreating(false);
    }
  }

  const checkNow = useCallback(async () => {
    if (!checkout?.providerShipmentId) return;
    // We don't have the shipment id stored separately from the intent —
    // reuse whatever id we last fetched.
    if (!intent) return;
    try {
      const res = await adminApi.checkProviderPayment(intent.id);
      setIntent(res.intent);
      if (res.intent.order_creation_uncertain === 1) {
        const cand = await adminApi.getRecoveryCandidates(intent.id);
        setCandidates(cand.candidates);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't check payment status.");
    }
  }, [checkout, intent]);

  // Auto-poll while waiting, bounded by POLL_TIMEOUT_MS, stops immediately once resolved.
  useEffect(() => {
    if (!intent) return;
    if (!["CHECKOUT_CREATED", "AWAITING_PAYMENT"].includes(intent.status)) return;
    if (pollStartedAt.current && Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) return;
    const timer = setTimeout(checkNow, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [intent, checkNow]);

  const timedOut = intent && ["CHECKOUT_CREATED", "AWAITING_PAYMENT"].includes(intent.status) && pollStartedAt.current && Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS;

  return (
    <div className="stack">
      <h2>Parcel2Go sandbox test</h2>
      <p className="field-help">
        Creates ONE real sandbox order with Parcel2Go using obviously-fake test data, so you can validate the hosted
        checkout flow described in <code>docs/parcel2go-sandbox-testing-guide.md</code>. No real money moves — this
        only works against <code>sandbox.parcel2go.com</code> unless you've explicitly overridden that locally.
      </p>

      {error && (
        <div className="banner banner-danger" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="card stack-sm" style={{ border: "2px solid var(--color-danger, #b91c1c)" }}>
        <strong>⚠ LIVE Parcel2Go check — real account, quotes only</strong>
        <p className="field-help" style={{ margin: 0 }}>
          Uses separate <code>PARCEL2GO_LIVE_CLIENT_ID</code>/<code>SECRET</code> credentials against Parcel2Go's real
          production host. This button can only ever call <code>POST /quotes</code> — there is no code path here to
          create an order, take a payment, or fetch a label (see{" "}
          <code>functions/lib/parcel2go-live-quote-check.ts</code>). Fixed test parcel: SW1A 1AA → PO14 1AS, 1kg,
          30×20×10cm. Compare the carriers/services/prices below against the sandbox list further down — the
          sandbox catalogue is confirmed stale/legacy test data (Hermes/MyHermes branding, no Royal Mail).
        </p>
        <button className="btn btn-primary" style={{ width: "fit-content" }} onClick={loadLiveQuotesPreview} disabled={loadingLive}>
          {loadingLive ? "Checking live quotes…" : "Get LIVE quotes (read-only)"}
        </button>
        {liveResult && (
          <div className={`banner ${liveResult.ok ? "banner-success" : "banner-warn"}`}>
            <div className="stack-sm" style={{ width: "100%" }}>
              <span>{liveResult.detail}</span>
              {liveResult.raw != null && (
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: "0.8rem", maxHeight: 400, overflow: "auto" }}>
                  {JSON.stringify(liveResult.raw, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card stack-sm">
        <strong>1. Check connection</strong>
        <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={checkConnection} disabled={checking}>
          {checking ? "Checking…" : "Check Parcel2Go connection"}
        </button>
        {health && (
          <div className={`banner ${health.ok ? "banner-success" : "banner-warn"}`}>
            <span>{health.ok ? "Connected." : health.detail}</span>
          </div>
        )}
      </div>

      <div className="card stack-sm">
        <strong>2. Create a sandbox test order</strong>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <button className="btn btn-primary" style={{ width: "fit-content" }} onClick={() => createTest()} disabled={creating}>
            {creating ? "Creating…" : "Create sandbox test order (cheapest)"}
          </button>
          <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={loadQuotesPreview} disabled={loadingPreview}>
            {loadingPreview ? "Loading…" : "Show all available services"}
          </button>
        </div>

        {previewQuotes && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Carrier</th>
                  <th>Service</th>
                  <th>Price</th>
                  <th>Printer</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {previewQuotes.map((q) => (
                  <tr key={`${q.carrier}:${q.service}`}>
                    <td>{q.carrierDisplayName}</td>
                    <td>{q.serviceDisplayName}</td>
                    <td>£{q.priceGbp.toFixed(2)}</td>
                    <td>{q.requiresPrinter ? "Home print required" : q.printerlessAvailable ? "Printerless available" : "—"}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ width: "auto" }} onClick={() => createTest({ carrier: q.carrier, service: q.service })} disabled={creating}>
                        Test this
                      </button>
                    </td>
                  </tr>
                ))}
                {previewQuotes.length === 0 && (
                  <tr>
                    <td colSpan={5}>No quotes returned for the test parcel.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {quote && (
          <div className="field-help">
            Quote: {quote.carrierDisplayName} — {quote.serviceDisplayName}, £{quote.priceGbp.toFixed(2)}, {quote.estimatedDeliveryDays}
            {quote.isMock && " (MOCK — check your Parcel2Go credentials are set)"}
          </div>
        )}

        {checkout && (
          <div className="banner banner-info">
            <div className="stack-sm">
              <strong>Ready — open this in a new tab:</strong>
              <a href={checkout.redirectUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ width: "fit-content" }}>
                Open Parcel2Go checkout ↗
              </a>
              <code style={{ wordBreak: "break-all", fontSize: "0.8rem" }}>{checkout.redirectUrl}</code>
            </div>
          </div>
        )}
      </div>

      {checkout && (
        <div className="card stack-sm">
          <strong>3. While you're there — observe and note</strong>
          <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
            {OBSERVATION_CHECKLIST.map((item) => (
              <li key={item} style={{ marginBottom: "var(--space-1)" }}>
                {item}
              </li>
            ))}
          </ul>
          <p className="field-help" style={{ margin: 0 }}>
            Don't paste card numbers or screenshot the payment form — only note the structural facts above.
          </p>
        </div>
      )}

      {intent && (
        <div className="card stack-sm">
          <strong>4. Payment status</strong>
          <div>Shipment status: {intent.status}</div>

          {["CHECKOUT_CREATED", "AWAITING_PAYMENT"].includes(intent.status) && (
            <div className="banner banner-warn">
              <span>Waiting for Parcel2Go to confirm payment… {timedOut ? "(auto-check timed out — use the button below)" : "(auto-checking every 10s)"}</span>
            </div>
          )}

          <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={checkNow}>
            Check payment status now
          </button>

          {intent.status === "READY_FOR_DROPOFF" && (
            <div className="banner banner-success stack-sm">
              <strong>Paid and label retrieved.</strong>
              {intent.label_json &&
                (() => {
                  const label = JSON.parse(intent.label_json) as { url: string | null; qrPayload: string | null };
                  if (label.url) {
                    return (
                      <a href={toOpenableUrl(label.url)} target="_blank" rel="noopener noreferrer">
                        Open label
                      </a>
                    );
                  }
                  if (label.qrPayload) {
                    // Print-in-store service (Evri/Yodel Direct) — there is
                    // no downloadable label at all, only this barcode the
                    // shop scans to print the carrier's own label. See
                    // shared/providers/parcel2go-provider.ts getLabel().
                    return (
                      <span className="field-help">
                        Print-in-store barcode (no downloadable label for this service): <code>{label.qrPayload}</code>
                      </span>
                    );
                  }
                  return null;
                })()}
            </div>
          )}

          {intent.status === "FAILED" && intent.order_creation_uncertain !== 1 && (
            <div className="banner banner-danger">
              <span>{intent.error_json ? (JSON.parse(intent.error_json) as { message: string }).message : "Failed."}</span>
            </div>
          )}

          {intent.order_creation_uncertain === 1 && (
            <div className="banner banner-danger stack-sm">
              <strong>Order creation was uncertain — we don't know if an order exists at Parcel2Go.</strong>
              <span>Never auto-retrying. Review possible matches below.</span>
              {candidates && candidates.length === 0 && <span>No matching recent orders found — safe to clear and try again.</span>}
              {candidates &&
                candidates.map((c) => (
                  <div key={c.orderId} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>
                      Order {c.orderId} — {c.serviceSlug}, £{c.valueGbp.toFixed(2)} declared value, {c.paidDate ? `paid ${c.paidDate}` : "unpaid"}
                    </span>
                    <button
                      className="btn btn-secondary"
                      style={{ width: "auto" }}
                      onClick={async () => {
                        const res = await adminApi.linkRecoveredOrder(intent.id, c);
                        setIntent(res.intent);
                        setCandidates(null);
                      }}
                    >
                      This is it — link it
                    </button>
                  </div>
                ))}
              <button
                className="btn btn-ghost"
                style={{ width: "fit-content" }}
                onClick={async () => {
                  const res = await adminApi.clearUncertain(intent.id);
                  setIntent(res.intent);
                  setCandidates(null);
                }}
              >
                None of these match — safe to retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
